import { Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { TenantConfig } from '@/shared/interfaces/tenant';
import type { MinimumTopUpAmount } from '@/shared/interfaces/balance';
import type { WalletPackage } from '@/shared/interfaces/wallet';
import useWallet from '@/modules/wallet/composables/useWallet';
import useTopUp from '@/modules/top-up/composables/useTopUp';
import { useSharedStore } from '@/stores/shared/sharedStore';

const useQuickTopUp = (tenantConfig: Ref<TenantConfig | undefined>, minimumTopUp: Ref<MinimumTopUpAmount | undefined>) => {
	const sharedStore = useSharedStore();
	const { getWalletPackagesApiCall, selectedWallet } = useWallet();
	const { selectTopUp, getApplicablePaymentCountry } = useTopUp(tenantConfig, minimumTopUp);

	const quickPackages = ref<WalletPackage[]>([]);
	const isLoadingQuickPackages = ref(false);

	const fetchQuickPackages = async (walletId: string) => {
		if (!walletId) return;
		isLoadingQuickPackages.value = true;
		try {
			const packages = await getWalletPackagesApiCall(walletId);
			quickPackages.value = packages.filter((p: WalletPackage) => p.show_as_quick_topup);
		} catch (e) {
			console.log('[QuickTopUp] Failed to fetch quick packages', e);
		} finally {
			isLoadingQuickPackages.value = false;
		}
	};

	const handleQuickTopUp = (walletPackage: WalletPackage, encryptedRfidTag?: string, loginToken?: string) => {
		const paymentMethodCountry = getApplicablePaymentCountry();
		const walletId = selectedWallet.value?.id?.toString();

		console.log('[QuickTopUp] handleQuickTopUp called:', {
			packageId: walletPackage.id,
			packageName: walletPackage.name,
			paymentMethodCountry,
			walletId,
			encryptedRfidTag: encryptedRfidTag || undefined,
		});

		sharedStore.shouldUsePreferredPaymentMode = false;
		sharedStore.shouldUsePayconiq = false;

		selectTopUp(walletPackage.id, paymentMethodCountry, null, encryptedRfidTag, loginToken);
	};

	return {
		quickPackages,
		isLoadingQuickPackages,
		fetchQuickPackages,
		handleQuickTopUp,
	};
};

export default useQuickTopUp;
